import React from 'react';
import { Typography, Button, ButtonPro } from 'components';
import moment from 'moment';
import './index.scss';

interface EmploymentHistoryData {
  id?: string;
  employeeNameWithInitials?: string | null;
  designationName?: string | null;
  joinedDate?: string | null;
  confirmationDate?: string | null;
  status?: string | null;
  terminationDate?: string | null;
  terminatedBy?: string | null;
  terminatedReason?: string | null;
  employeeProjects?: Array<{
    id: string;
    projects: { name: string };
    roleInProject: string;
    startDate: string | null;
    endDate?: string | null;
    isCurrent: boolean;
    notes?: string;
  }> | null;
  [key: string]: any;
}

interface EmploymentHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  employeeData: EmploymentHistoryData | null;
}

export const EmploymentHistorySidebar: React.FC<
  EmploymentHistorySidebarProps
> = ({ isOpen, onClose, employeeData }) => {
  const formatDate = (date: string | null | undefined) =>
    date ? moment.utc(date).format('DD/MM/YYYY') : 'N/A';

  const calculateTenure = (
    joinedDate?: string | null,
    terminationDate?: string | null
  ) => {
    if (!joinedDate) return 'N/A';

    const start = moment(joinedDate);
    const end = terminationDate ? moment(terminationDate) : moment();
    const duration = moment.duration(end.diff(start));

    const years = Math.floor(duration.asYears());
    const months = Math.floor(duration.asMonths() % 12);

    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${months} month${
        months !== 1 ? 's' : ''
      }`;
    } else {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
  };

  const getEmploymentStatus = () => {
    if (!employeeData) return { status: 'Unknown', color: '#6c757d' };

    switch (employeeData.status?.toLowerCase()) {
      case 'active':
        return { status: 'Active', color: '#28a745' };
      case 'terminated':
        return { status: 'Terminated', color: '#dc3545' };
      case 'probation':
        return { status: 'On Probation', color: '#ffc107' };
      default:
        return { status: employeeData.status || 'Unknown', color: '#6c757d' };
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`employment-history-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className={`employment-history-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Typography
            text="Employment History"
            type="base_regular"
            weight="bold"
            className="sidebar-title"
          />
          <ButtonPro
            label="×"
            type="icon"
            size="small"
            onClick={onClose}
            buttonClassNames="close-button"
          />
        </div>

        <div className="sidebar-content">
          {employeeData ? (
            <>
              {/* Employee Overview */}
              <div className="history-section">
                <Typography
                  text="Employee Overview"
                  type="small_regular"
                  weight="bold"
                  className="section-title"
                />
                <div className="overview-card">
                  <div className="employee-name">
                    {employeeData.employeeNameWithInitials || 'N/A'}
                  </div>
                  <div className="employee-designation">
                    {employeeData.designationName || 'N/A'}
                  </div>
                  <div className="employment-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getEmploymentStatus().color }}
                    >
                      {getEmploymentStatus().status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Employment Timeline */}
              <div className="history-section">
                <Typography
                  text="Employment Timeline"
                  type="small_regular"
                  weight="bold"
                  className="section-title"
                />
                <div className="timeline-item">
                  <div className="timeline-date">
                    {formatDate(employeeData.joinedDate)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-title">Joined Company</div>
                    <div className="timeline-description">
                      Started as {employeeData.designationName || 'Employee'}
                    </div>
                  </div>
                </div>

                {employeeData.confirmationDate && (
                  <div className="timeline-item">
                    <div className="timeline-date">
                      {formatDate(employeeData.confirmationDate)}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-title">Confirmation</div>
                      <div className="timeline-description">
                        Employment confirmed
                      </div>
                    </div>
                  </div>
                )}

                {employeeData.status === 'Terminated' &&
                  employeeData.terminationDate && (
                    <div className="timeline-item terminated">
                      <div className="timeline-date">
                        {formatDate(employeeData.terminationDate)}
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-title">Termination</div>
                        <div className="timeline-description">
                          {employeeData.terminatedReason ||
                            'Employment terminated'}
                        </div>
                        {employeeData.terminatedBy && (
                          <div className="timeline-meta">
                            Terminated by: {employeeData.terminatedBy}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* Tenure Information */}
              <div className="history-section">
                <Typography
                  text="Tenure Information"
                  type="small_regular"
                  weight="bold"
                  className="section-title"
                />
                <div className="tenure-card">
                  <div className="tenure-item">
                    <div className="tenure-label">Total Tenure</div>
                    <div className="tenure-value">
                      {calculateTenure(
                        employeeData.joinedDate || undefined,
                        employeeData.terminationDate || undefined
                      )}
                    </div>
                  </div>
                  <div className="tenure-item">
                    <div className="tenure-label">Join Date</div>
                    <div className="tenure-value">
                      {formatDate(employeeData.joinedDate)}
                    </div>
                  </div>
                  {employeeData.terminationDate && (
                    <div className="tenure-item">
                      <div className="tenure-label">End Date</div>
                      <div className="tenure-value">
                        {formatDate(employeeData.terminationDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Project History Summary */}
              {(employeeData.employeeProjects?.length ?? 0) > 0 && (
                <div className="history-section">
                  <Typography
                    text="Project History Summary"
                    type="small_regular"
                    weight="bold"
                    className="section-title"
                  />
                  <div className="project-summary">
                    <div className="summary-stat">
                      <div className="stat-number">
                        {employeeData.employeeProjects?.length ?? 0}
                      </div>
                      <div className="stat-label">Total Projects</div>
                    </div>
                    <div className="summary-stat">
                      <div className="stat-number">
                        {employeeData.employeeProjects?.filter(
                          (p) => p.isCurrent
                        ).length ?? 0}
                      </div>
                      <div className="stat-label">Current Projects</div>
                    </div>
                  </div>

                  <div className="recent-projects">
                    <Typography
                      text="Recent Projects"
                      type="small_regular"
                      weight="bold"
                      className="subsection-title"
                    />
                    {(employeeData.employeeProjects
                      ? [...employeeData.employeeProjects]
                      : []
                    )
                      .sort((a, b) => {
                        const dateA = a.startDate
                          ? new Date(a.startDate).getTime()
                          : 0;
                        const dateB = b.startDate
                          ? new Date(b.startDate).getTime()
                          : 0;
                        return dateB - dateA;
                      })
                      .slice(0, 3)
                      .map((project) => (
                        <div key={project.id} className="project-item">
                          <div className="project-name">
                            {project.projects?.name}
                            {project.isCurrent && (
                              <span className="current-badge">Current</span>
                            )}
                          </div>
                          <div className="project-role">
                            {project.roleInProject}
                          </div>
                          <div className="project-duration">
                            {formatDate(project.startDate)} -{' '}
                            {project.endDate
                              ? formatDate(project.endDate)
                              : 'Present'}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Future Actions */}
              <div className="history-section">
                <Typography
                  text="Actions"
                  type="small_regular"
                  weight="bold"
                  className="section-title"
                />
                <div className="action-buttons">
                  <Button type="primary" size="sm">
                    Export History
                  </Button>
                  <Button type="secondary" size="sm">
                    Print History
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-data">
              <Typography
                text="No employment data available"
                type="small_regular"
                color="gray"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
